import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { SiteHeader } from "@/components/site/SiteHeader";
import { SiteFooter } from "@/components/site/SiteFooter";
import { CiteThis } from "@/components/site/CiteThis";
import { SITE } from "@/lib/site";

const BUSINESS_TYPES = [
  "LocalBusiness",
  "Restaurant",
  "Store",
  "MedicalBusiness",
  "Dentist",
  "ProfessionalService",
  "HomeAndConstructionBusiness",
  "LegalService",
  "AutomotiveBusiness",
  "BeautySalon",
  "HairSalon",
  "HealthClub",
  "Lodging",
  "FinancialService",
] as const;

const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"] as const;

export const Route = createFileRoute("/tools/local-schema/")({
  head: () => {
    const url = `${SITE.url}/tools/local-schema`;
    return {
      meta: [
        { title: `Local Business Schema Generator — Free JSON-LD Builder | ${SITE.name}` },
        {
          name: "description",
          content:
            "Generate valid LocalBusiness JSON-LD schema with NAP, hours, geo coordinates, and sameAs links. Free, client-side, copy-paste into your site head.",
        },
        { property: "og:title", content: `Local Schema Generator | ${SITE.name}` },
        { property: "og:url", content: url },
        { property: "og:type", content: "website" },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "SoftwareApplication",
            name: "Local Business Schema Generator",
            applicationCategory: "DeveloperApplication",
            operatingSystem: "Web",
            url,
            offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
            publisher: { "@type": "Organization", name: SITE.name, url: SITE.url },
          }),
        },
      ],
    };
  },
  component: SchemaGenerator,
});

interface HoursRow {
  day: string;
  open: string;
  close: string;
  closed: boolean;
}

function SchemaGenerator() {
  const [type, setType] = useState<string>("LocalBusiness");
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [url, setUrl] = useState("");
  const [streetAddress, setStreetAddress] = useState("");
  const [locality, setLocality] = useState("");
  const [region, setRegion] = useState("");
  const [postalCode, setPostalCode] = useState("");
  const [country, setCountry] = useState("US");
  const [lat, setLat] = useState("");
  const [lng, setLng] = useState("");
  const [priceRange, setPriceRange] = useState("$$");
  const [image, setImage] = useState("");
  const [sameAs, setSameAs] = useState("");
  const [hours, setHours] = useState<HoursRow[]>(
    DAYS.map((d) => ({ day: d, open: "09:00", close: "17:00", closed: false })),
  );
  const [copied, setCopied] = useState(false);

  function updateHour(i: number, patch: Partial<HoursRow>) {
    setHours((rows) => rows.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  }

  const schema = useMemo(() => {
    const out: Record<string, unknown> = {
      "@context": "https://schema.org",
      "@type": type,
    };
    if (name) out.name = name;
    if (image) out.image = image;
    if (url) out.url = url;
    if (phone) out.telephone = phone;
    if (priceRange) out.priceRange = priceRange;
    if (streetAddress || locality || region || postalCode) {
      out.address = {
        "@type": "PostalAddress",
        ...(streetAddress && { streetAddress }),
        ...(locality && { addressLocality: locality }),
        ...(region && { addressRegion: region }),
        ...(postalCode && { postalCode }),
        ...(country && { addressCountry: country }),
      };
    }
    if (lat && lng) {
      out.geo = {
        "@type": "GeoCoordinates",
        latitude: Number(lat),
        longitude: Number(lng),
      };
    }
    const openingHours = hours
      .filter((h) => !h.closed && h.open && h.close)
      .map((h) => ({
        "@type": "OpeningHoursSpecification",
        dayOfWeek: h.day,
        opens: h.open,
        closes: h.close,
      }));
    if (openingHours.length) out.openingHoursSpecification = openingHours;
    const same = sameAs
      .split(/\n+/)
      .map((s) => s.trim())
      .filter(Boolean);
    if (same.length) out.sameAs = same;
    return out;
  }, [type, name, image, url, phone, priceRange, streetAddress, locality, region, postalCode, country, lat, lng, hours, sameAs]);

  const jsonString = useMemo(
    () => `<script type="application/ld+json">\n${JSON.stringify(schema, null, 2)}\n</script>`,
    [schema],
  );

  async function copy() {
    try {
      await navigator.clipboard.writeText(jsonString);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      // ignore
    }
  }

  function openValidator() {
    const code = encodeURIComponent(JSON.stringify(schema));
    window.open(`https://search.google.com/test/rich-results?user_agent=2&code=${code}`, "_blank", "noopener");
  }

  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <main className="mx-auto max-w-6xl px-5 sm:px-8 py-16">
        <nav aria-label="Breadcrumb" className="text-xs uppercase tracking-wider text-muted-foreground">
          <ol className="flex items-center gap-2 flex-wrap">
            <li><Link to="/" className="hover:text-accent">Home</Link></li>
            <li aria-hidden>/</li>
            <li><Link to="/tools" className="hover:text-accent">Tools</Link></li>
            <li aria-hidden>/</li>
            <li>Local Schema Generator</li>
          </ol>
        </nav>
        <header className="mt-6 max-w-3xl">
          <p className="eyebrow">Tool</p>
          <h1 className="headline-display mt-3 text-5xl">Local Business schema generator</h1>
          <p className="mt-4 text-lg text-muted-foreground">
            Fill the form, copy the JSON-LD, drop it inside the <code className="text-sm">&lt;head&gt;</code> of your site. Validate one click later. Free, client-side, no signup.
          </p>
        </header>

        <div className="mt-8 flex items-center gap-3 border-t border-b border-border py-4">
          <CiteThis title="Local Schema Generator" url={`${SITE.url}/tools/local-schema`} />
        </div>

        <div className="mt-10 grid gap-8 lg:grid-cols-2">
          <form className="space-y-6" onSubmit={(e) => e.preventDefault()}>
            <Field label="Business type">
              <select value={type} onChange={(e) => setType(e.target.value)} className="form-input">
                {BUSINESS_TYPES.map((t) => <option key={t}>{t}</option>)}
              </select>
            </Field>
            <Field label="Business name"><input className="form-input" value={name} onChange={(e) => setName(e.target.value)} /></Field>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Phone"><input className="form-input" value={phone} onChange={(e) => setPhone(e.target.value)} /></Field>
              <Field label="Website URL"><input className="form-input" value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://" /></Field>
            </div>
            <Field label="Image URL"><input className="form-input" value={image} onChange={(e) => setImage(e.target.value)} placeholder="https://" /></Field>
            <Field label="Street address"><input className="form-input" value={streetAddress} onChange={(e) => setStreetAddress(e.target.value)} /></Field>
            <div className="grid grid-cols-2 gap-4">
              <Field label="City"><input className="form-input" value={locality} onChange={(e) => setLocality(e.target.value)} /></Field>
              <Field label="State / region"><input className="form-input" value={region} onChange={(e) => setRegion(e.target.value)} /></Field>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Postal code"><input className="form-input" value={postalCode} onChange={(e) => setPostalCode(e.target.value)} /></Field>
              <Field label="Country (ISO)"><input className="form-input" value={country} onChange={(e) => setCountry(e.target.value)} /></Field>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Latitude"><input className="form-input" value={lat} onChange={(e) => setLat(e.target.value)} placeholder="40.7128" /></Field>
              <Field label="Longitude"><input className="form-input" value={lng} onChange={(e) => setLng(e.target.value)} placeholder="-74.0060" /></Field>
            </div>
            <Field label="Price range"><input className="form-input" value={priceRange} onChange={(e) => setPriceRange(e.target.value)} placeholder="$$" /></Field>
            <Field label="sameAs (one URL per line)">
              <textarea className="form-input min-h-[100px]" value={sameAs} onChange={(e) => setSameAs(e.target.value)} placeholder={`https://facebook.com/yourbiz\nhttps://www.instagram.com/yourbiz`} />
            </Field>
            <fieldset>
              <legend className="eyebrow mb-2">Opening hours</legend>
              <div className="space-y-2">
                {hours.map((h, i) => (
                  <div key={h.day} className="grid grid-cols-[110px_1fr_1fr_auto] items-center gap-3">
                    <span className="text-sm">{h.day}</span>
                    <input type="time" disabled={h.closed} value={h.open} onChange={(e) => updateHour(i, { open: e.target.value })} className="form-input disabled:opacity-50" />
                    <input type="time" disabled={h.closed} value={h.close} onChange={(e) => updateHour(i, { close: e.target.value })} className="form-input disabled:opacity-50" />
                    <label className="flex items-center gap-2 text-xs uppercase tracking-wider">
                      <input type="checkbox" checked={h.closed} onChange={(e) => updateHour(i, { closed: e.target.checked })} />
                      Closed
                    </label>
                  </div>
                ))}
              </div>
            </fieldset>
          </form>

          <div className="lg:sticky lg:top-8 lg:self-start">
            <p className="eyebrow">Generated JSON-LD</p>
            <pre className="mt-3 max-h-[600px] overflow-auto rounded-sm border border-border bg-paper-soft p-4 text-xs leading-relaxed font-mono">
              {jsonString}
            </pre>
            <div className="mt-4 flex flex-wrap gap-3">
              <button type="button" onClick={copy} className="rounded-sm border border-foreground bg-foreground px-4 py-2 text-xs uppercase tracking-wider text-background hover:bg-accent hover:border-accent transition-colors">
                {copied ? "Copied!" : "Copy schema"}
              </button>
              <button type="button" onClick={openValidator} className="rounded-sm border border-border bg-paper-soft px-4 py-2 text-xs uppercase tracking-wider hover:border-foreground transition-colors">
                Validate in Rich Results →
              </button>
            </div>
            <p className="mt-4 text-xs text-muted-foreground">
              Schema is generated entirely in your browser — nothing is sent to our servers.
            </p>
          </div>
        </div>
      </main>
      <style>{`.form-input { width: 100%; border: 1px solid hsl(var(--border)); background: hsl(var(--paper-soft, var(--background))); border-radius: 2px; padding: 0.625rem 0.75rem; font-size: 0.875rem; }`}</style>
      <SiteFooter />
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="eyebrow block mb-1">{label}</span>
      {children}
    </label>
  );
}
